import { Typography } from "@mui/material";
import { TreeItem, TreeView } from "@mui/x-tree-view";
import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { t } from "ttag";
import Checkbox from "../../../cool_widgets/CoolCheckbox";
import { ArrowDownO, CheckboxChecked } from "../../../icons";
import useStyles from "../SiteManagement.style";
import useSetupStyles from "./SiteSetup.style";

// Package trees and checkbox with the same look as the packages on Edit Site
const rootNodeId = "Professional Packages";

const TreeIcons = (styles: any) => ({
  defaultCollapseIcon: <><div className={styles.arrowContainer} /> <ArrowDownO className={styles.arrow} /> </>,
  defaultExpandIcon: <><div className={styles.arrowContainer} /> <ArrowDownO className={clsx(styles.arrow, styles.rotateArrow)} /></>,
  defaultEndIcon: <div className={styles.arrowContainer} />
});

// Keeps a checkbox click from also opening or closing the tree item
const stopClick = (event: any) => {
  event.stopPropagation();
};

// The small checkbox the package trees use; stops the click from reaching the tree item
export const PackageCheckbox = (props: any) => {
  const styles: any = useStyles();
  const setupStyles: any = useSetupStyles();
  return (
    <Checkbox
      color="default"
      edge="end"
      variant="outlined"
      onClick={stopClick}
      checkedIcon={<CheckboxChecked className={setupStyles.packageCheckedIcon} />}
      className={styles.smallCheckbox}
      {...props}
    />
  );
};

const PackageLabel = ({ text, checkbox }: any) => {
  const styles: any = useStyles();
  return (
    <div className={styles.labelRoot}>
      {checkbox}
      <Typography variant="body2" component="span" className={styles.labelText}>
        {text}
      </Typography>
    </div>
  );
};

// Collapsible Professional Packages tree; packages: [{ name, label, checked }], onChange(name, checked)
const ProfessionalPackagesTree = ({ packages, onChange }: any) => {
  const styles: any = useStyles();
  const setupStyles: any = useSetupStyles();

  const allChecked = packages.every((option: any) => option.checked);
  const someChecked = packages.some((option: any) => option.checked);

  const handleAllChange = (event: any) => {
    packages.forEach((option: any) => onChange(option.name, event.target.checked));
  };

  const handleOptionChange = (name: any) => (event: any) => {
    onChange(name, event.target.checked);
  };

  return (
    <div>
      <TreeView
        className={styles.root}
        defaultExpanded={[rootNodeId]}
        {...TreeIcons(styles)}
      >
        <TreeItem
          nodeId={rootNodeId}
          label={
            <PackageLabel
              text={t`Professional Packages`}
              checkbox={
                <PackageCheckbox
                  checked={allChecked}
                  indeterminate={someChecked && !allChecked}
                  onChange={handleAllChange}
                />
              }
            />
          }
          className={setupStyles.treeItem}
          classes={{
            root: styles.root,
            content: styles.customerContent,
            expanded: styles.expanded,
            selected: styles.selected,
            group: styles.customerGroup,
            label: styles.label,
            iconContainer: styles.customerIconContainer
          }}
        >
          {packages.map((option: any) => (
            <TreeItem
              key={option.name}
              nodeId={option.name}
              label={
                <PackageLabel
                  text={option.label}
                  checkbox={
                    <PackageCheckbox
                      checked={option.checked}
                      onChange={handleOptionChange(option.name)}
                    />
                  }
                />
              }
              className={setupStyles.treeItem}
              classes={{
                root: styles.root,
                content: styles.content,
                expanded: styles.expanded,
                selected: styles.selected,
                group: styles.group,
                label: styles.label,
                iconContainer: styles.iconContainer
              }}
            />
          ))}
        </TreeItem>
      </TreeView>
    </div>
  );
};

// Collapsible one-item tree for Support Debug / Trial Package; clicking the row opens or closes it.
// children are shown when it's expanded; onChange(checked)
export const SinglePackageTree = ({ nodeId, label, checked, onChange, children }: any) => {
  const styles: any = useStyles();
  const setupStyles: any = useSetupStyles();
  const [expanded, setExpanded] = useState(checked);

  // Ticking opens it so the date shows; unticking leaves it as it is
  useEffect(() => {
    if (checked) {
      setExpanded(true);
    }
  }, [checked]);

  const handleToggle = (event: any, nodeIds: any) => {
    setExpanded(nodeIds.includes(nodeId));
  };

  const handleCheck = (event: any) => {
    onChange(event.target.checked);
  };

  return (
    <TreeView
      className={styles.root}
      expanded={expanded ? [nodeId] : []}
      onNodeToggle={handleToggle}
      {...TreeIcons(styles)}
    >
      <TreeItem
        nodeId={nodeId}
        label={
          <PackageLabel
            text={label}
            checkbox={<PackageCheckbox checked={checked} onChange={handleCheck} />}
          />
        }
        className={setupStyles.treeItem}
        classes={{
          root: styles.root,
          content: styles.customerContent,
          expanded: styles.expanded,
          selected: styles.selected,
          group: styles.customerGroup,
          label: styles.label,
          iconContainer: styles.customerIconContainer
        }}
      >
        {children && <div className={setupStyles.packageContent}>{children}</div>}
      </TreeItem>
    </TreeView>
  );
};

export default ProfessionalPackagesTree;
